/**
 * useQuote Hook
 * 
 * React hook for managing quotes in components.
 * Provides easy-to-use functions for creating, sharing, and managing quotes.
 * Uses human-readable IDs and /cotizacion/ URLs.
 */

import { useState, useCallback, useEffect } from 'react';
import { useApp } from '@/context/AppContext';
import { 
  createUserQuote, 
  retrieveUserQuote, 
  updateQuoteStage,
  shareQuote,
  generateShareMessage,
  markQuoteUsed,
  restoreFromQuote,
  isCotizacionMode,
  isQuoteExpired,
  hasValidSnapshot,
  isLegacyQuote,
  QuoteData,
  CreateQuoteParams,
  BundleInfo
} from '@/services/quoteService';

interface UseQuoteReturn {
  // State
  currentQuote: QuoteData | null;
  isLoading: boolean;
  error: string | null;
  
  // Actions  
  createQuote: (params: CreateQuoteParams) => Promise<QuoteData | null>;
  loadQuote: (quoteId: string) => Promise<QuoteData | null>;
  updateStage: (stage: 'productos' | 'plan-seleccion' | 'carrito', bundleInfo?: BundleInfo) => Promise<void>;
  shareQuoteUrl: () => Promise<void>;
  getShareMessage: () => string;
  markAsUsed: () => Promise<void>;
  restoreState: () => void;
  
  // Utilities
  isInCotizacionMode: boolean;
  quoteId: string | null;
}

export const useQuote = (): UseQuoteReturn => {
  const { state, updateEleccionProgress, updateDispositivo } = useApp();
  const [currentQuote, setCurrentQuote] = useState<QuoteData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Check if we're in cotización mode
  const quoteId = isCotizacionMode();
  const isInCotizacionMode = !!quoteId;

  /**
   * Create a new quote
   */
  const createQuote = useCallback(async (params: CreateQuoteParams): Promise<QuoteData | null> => {
    setIsLoading(true);
    setError(null);
    
    try {
      const quote = await createUserQuote(state, params);
      setCurrentQuote(quote);
      return quote;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create quote';
      setError(errorMessage);
      console.error('useQuote - createQuote error:', err);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [state]);

  /**
   * Load an existing quote with expiration-aware error handling and legacy support
   */
  const loadQuote = useCallback(async (id: string): Promise<QuoteData | null> => {
    setIsLoading(true);
    setError(null);
    
    try {
      const quote = await retrieveUserQuote(id);
      
      if (!quote) {
        setError('Quote not found or may have been removed');
        return null;
      }
      
      // Detect legacy quotes and provide helpful info
      const { isLegacy, missingFields } = isLegacyQuote(quote);
      if (isLegacy) {
        console.log(`🔄 Loading legacy quote: ${id}`, { missingFields });
        
        // Show informational message for legacy quotes without snapshots
        if (missingFields.includes('priceSnapshot')) {
          console.log('💡 Legacy quote will use live pricing');
        }
        
        // Legacy quotes without timestamps are treated as non-expiring
        if (missingFields.includes('timestamp')) {
          console.log('⚠️ Legacy quote without timestamp - treated as non-expiring');
        }
      }
      
      // Check if quote has expired and provide user-friendly feedback
      // Legacy quotes without timestamps are handled gracefully in the service
      if (quote.status === 'expired') {
        console.log(`⏰ Loaded expired quote: ${id}`);
        setError('This quote has expired after 7 days. Please create a new quote.');
      }
      
      // Validate snapshot data if present
      if (quote.priceSnapshot && !hasValidSnapshot(quote)) {
        console.log('📈 Quote snapshot invalid - will use live pricing');
      }
      
      setCurrentQuote(quote);
      return quote;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load quote';
      setError(errorMessage);
      console.error('useQuote - loadQuote error:', err);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Update current quote stage
   */
  const updateStage = useCallback(async (
    stage: 'productos' | 'plan-seleccion' | 'carrito',
    bundleInfo?: BundleInfo
  ): Promise<void> => {
    if (!currentQuote) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      await updateQuoteStage(currentQuote.id, stage, state, bundleInfo);
      
      // Update local quote data
      setCurrentQuote(prev => prev ? {
        ...prev,
        metadata: {
          ...prev.metadata,
          stage
        }
      } : null);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update quote';
      setError(errorMessage);
      console.error('useQuote - updateStage error:', err);
    } finally {
      setIsLoading(false);
    }
  }, [currentQuote, state]);

  /**
   * Share quote URL
   */
  const shareQuoteUrl = useCallback(async (): Promise<void> => {
    if (!currentQuote) {
      setError('No quote available to share');
      return;
    }
    
    try {
      await shareQuote(currentQuote);
      console.log('Quote URL copied to clipboard');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to share quote';
      setError(errorMessage);
      console.error('useQuote - shareQuoteUrl error:', err);
    }
  }, [currentQuote]);

  /**
   * Get shareable message
   */
  const getShareMessage = useCallback((): string => {
    if (!currentQuote) {
      return 'No quote available';
    }
    return generateShareMessage(currentQuote);
  }, [currentQuote]);

  /**
   * Mark quote as used
   */
  const markAsUsed = useCallback(async (): Promise<void> => {
    if (!currentQuote) return;
    
    try {
      await markQuoteUsed(currentQuote.id);
      
      // Update local quote data
      setCurrentQuote(prev => prev ? {
        ...prev,
        status: 'used'
      } : null);
    } catch (err) {
      console.error('useQuote - markAsUsed error:', err);
      // Don't set error state as this is not critical
    }
  }, [currentQuote]);

  /**
   * Restore app state from current quote with expiration checks
   */
  const restoreState = useCallback((): void => {
    if (!currentQuote) {
      setError('No quote available to restore');
      return;
    }
    
    // Check if the quote is expired before allowing restoration
    if (currentQuote.status === 'expired') {
      setError('Cannot restore from expired quote. Please create a new quote.');
      return;
    }
    
    try {
      restoreFromQuote(currentQuote, updateEleccionProgress, updateDispositivo);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to restore state';
      setError(errorMessage);
      console.error('useQuote - restoreState error:', err);
    }
  }, [currentQuote, updateEleccionProgress, updateDispositivo]);

  /**
   * Auto-load quote if in cotización mode
   */
  useEffect(() => {
    if (quoteId && !currentQuote) {
      loadQuote(quoteId);
    }
  }, [quoteId, currentQuote, loadQuote]);

  return {
    // State
    currentQuote,
    isLoading,
    error,
    
    // Actions
    createQuote,
    loadQuote,
    updateStage,
    shareQuoteUrl,
    getShareMessage,
    markAsUsed,
    restoreState,
    
    // Utilities
    isInCotizacionMode,
    quoteId
  };
};

// Export backwards-compatible alias
export const useSnapshot = useQuote;